import { ArrowRight, Plus, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { canonicalUnit, getCompatibleUnits } from '@/lib/inventory-units'
import type { StockItemRef } from '@/lib/recipeEditor'
import { IngredientPicker } from './IngredientPicker'

/** One swap row as the owner edits it (F-MOD-N14). */
export interface SwapDraft {
  key: string
  from: string
  to: string
}

let seq = 0
export const newSwap = (from = '', to = ''): SwapDraft => ({ key: `sw${++seq}`, from, to })

/** Why a swap row cannot be saved, or null. Mirrors ReplaceOptionSwaps on the server. */
export function swapProblem(s: SwapDraft, items: Record<string, StockItemRef>, all: SwapDraft[]): string | null {
  if (!s.from && !s.to) return null
  if (!s.from || !s.to) return 'Pick both items'
  if (s.from === s.to) return 'Pick two different items'
  const f = items[s.from]
  const t = items[s.to]
  if (f && t && !getCompatibleUnits(f.unit).includes(canonicalUnit(t.unit))) {
    return `${f.name} is in ${f.unit} and ${t.name} in ${t.unit}, so one can't stand in for the other`
  }
  if (all.some((o) => o.key !== s.key && o.from === s.from)) return `${f?.name ?? 'This item'} is swapped twice`
  return null
}

/**
 * "Swaps": on a line with this option, draw item B wherever the recipe would
 * draw item A — from the base or from another option (an extra shot's beans).
 * Shared by every item that carries the option's group.
 */
export function OptionSwaps({
  swaps,
  onChange,
  items,
  itemList,
  formatMoney,
}: {
  swaps: SwapDraft[]
  onChange: (s: SwapDraft[]) => void
  items: Record<string, StockItemRef>
  itemList: StockItemRef[]
  formatMoney: (n: number) => string
}) {
  const update = (key: string, patch: Partial<SwapDraft>) => onChange(swaps.map((s) => (s.key === key ? { ...s, ...patch } : s)))
  return (
    <div className="space-y-2 border-t pt-3">
      <div>
        <p className="text-sm font-medium">Swaps</p>
        <p className="text-xs text-muted-foreground">
          Use one ingredient instead of another on the whole item, including what other options add. For example
          Odyssey: Coffee Beans (Brazil) → Coffee Beans (Odyssey), so an extra shot uses Odyssey beans too.
        </p>
      </div>
      {swaps.map((s) => {
        const problem = swapProblem(s, items, swaps)
        return (
          <div key={s.key} className="space-y-1">
            <div className="flex flex-wrap items-center gap-2">
              <div className="min-w-[10rem] flex-1">
                <IngredientPicker value={s.from} items={itemList} onChange={(id) => update(s.key, { from: id })} formatMoney={formatMoney} />
              </div>
              <ArrowRight className="h-4 w-4 shrink-0 text-muted-foreground" aria-label="becomes" />
              <div className="min-w-[10rem] flex-1">
                <IngredientPicker value={s.to} items={itemList} onChange={(id) => update(s.key, { to: id })} formatMoney={formatMoney} />
              </div>
              <Button
                type="button"
                size="icon"
                variant="ghost"
                className="h-8 w-8 shrink-0"
                aria-label="Remove swap"
                onClick={() => onChange(swaps.filter((o) => o.key !== s.key))}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
            {problem && <p className="text-xs text-red-600 dark:text-red-400">{problem}</p>}
          </div>
        )
      })}
      <Button type="button" size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => onChange([...swaps, newSwap()])}>
        <Plus className="mr-1 h-3.5 w-3.5" /> Add swap
      </Button>
    </div>
  )
}
